import React, { useEffect, useState } from "react";
import Web3 from "web3";
import { useAppKitAccount } from "@reown/appkit/react";
import { useConnectorClient } from "wagmi";

import { NFTDataContract, NFTContarct } from "../config/contractConfig";
import NFTDataContractABI from "../abis/NFTDataContractABI.json";
import NFTABI from "../abis/NFTABI.json";

import Loader from "../components/Loader";
import Nodata from "../assets/img/no-record-found.png";

// 🔹 format USDT (18 decimals)
const formatUSDT = (v) => {
  if (!v) return "0.00";
  return (Number(v) / 1e18).toFixed(2);
};

const AllNFTDetails = () => {
  const { isConnected } = useAppKitAccount();
  const { data: client } = useConnectorClient();

  const [web3, setWeb3] = useState(null);
  const [nftList, setNftList] = useState([]);
  const [loadingNFT, setLoadingNFT] = useState(false);

  // ================= INIT WEB3 =================
  useEffect(() => {
    let provider = null;
    if (client?.transport) provider = client.transport;
    else if (window.ethereum) provider = window.ethereum;
    if (!provider) return;

    setWeb3(new Web3(provider));
  }, [client]);

  // ================= FETCH ALL NFTs =================
  const fetchAllNFTs = async () => {
    if (!web3) return;

    try {
      setLoadingNFT(true);

      const nftData = new web3.eth.Contract(
        NFTDataContractABI,
        NFTDataContract
      );

      const nftMain = new web3.eth.Contract(
        NFTABI,
        NFTContarct
      );

      // -----------------------------
      // 1️⃣ From NFTData → Contract Owned Listed
      // -----------------------------
      const listA = await nftData.methods
        .getContractOwnedListedTokens()
        .call(); // uint256[]

      // -----------------------------
      // 2️⃣ From NFT Main → Active Listed
      // -----------------------------
      const listB = await nftMain.methods
        .getAllActiveListedNFTs()
        .call(); 
      // returns [tokenIds, sellers, prices, images]

      const tokenIdsB = listB[0] || [];
      const sellersB  = listB[1] || [];
      const pricesB   = listB[2] || [];

      // -----------------------------
      // 3️⃣ MERGE TOKEN IDS (dedupe)
      // -----------------------------
      const tokenSet = new Set();

      listA.forEach(id => tokenSet.add(id.toString()));
      tokenIdsB.forEach(id => tokenSet.add(id.toString()));

      const finalTokenIds = Array.from(tokenSet);

      // -----------------------------
      // 4️⃣ FETCH DETAILS FOR EACH TOKEN
      // -----------------------------
      const details = await Promise.all(
        finalTokenIds.map(async (id) => {
          try {
            // try from active list first
            const idx = tokenIdsB.findIndex(t => t.toString() === id);

            if (idx >= 0) {
              return {
                tokenId: id,
                priceUSDT: pricesB[idx],
                ownerAddr: sellersB[idx],
              };
            }

            // fallback → NFTData.getNFTDetails
            const res = await nftData.methods
              .getNFTDetails(id)
              .call();

            return {
              tokenId: id,
              priceUSDT: res[0],
              ownerAddr: res[4],
            };
          } catch (e) {
            console.error("NFT detail error:", e);
            return null;
          }
        })
      );

      setNftList(details.filter(Boolean));
    } catch (e) {
      console.error("NFT Fetch Error:", e);
    } finally {
      setLoadingNFT(false);
    }
  };

  // ================= LOAD ON CONNECT =================
  useEffect(() => {
    if (web3 && isConnected) {
      fetchAllNFTs();
    }
  }, [web3, isConnected]);

  // ================= UI =================
  return (
    <div className="reserve-report p-3">
      <h4 className="mb-3">
        <span className="text-warning">NFT</span>{" "}
        <span className="fw-lighter">Details</span>
      </h4>

      <div className="card mt-3">
        <div className="card-body">
          <h4 className="mb-3">
            <span className="text-warning">All  Listed NFTs</span>
          </h4>

          <div className="table-responsive">
            <table className="table table-bordered table-striped">
              <thead className="table-dark">
                <tr>
                  <th>#</th>
                  <th>NFT ID</th>
                  <th>Price (USDT)</th>
                  <th>Owner</th>
                </tr>
              </thead>

              <tbody>
                {loadingNFT ? (
                  <tr>
                    <td colSpan="4" className="text-center">
                      <Loader />
                    </td>
                  </tr>
                ) : nftList.length ? (
                  nftList.map((nft, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td><b>#{nft.tokenId}</b></td>
                      <td>
                        {formatUSDT(nft.priceUSDT)}{" "}
                        <span className="text-success fw-semibold">
                          USDT
                        </span>
                      </td>
                      <td>
                        {nft.ownerAddr
                          ? nft.ownerAddr.slice(0, 6) +
                            "..." +
                            nft.ownerAddr.slice(-4)
                          : "--"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center py-4">
                      <img src={Nodata} alt="No Data" width="140" />
                      <p className="mt-2 text-muted fw-semibold">
                        No NFTs found
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllNFTDetails;
